/**
 * ShowTranscript — time-ordered message view.
 * In admin mode shows everything; in public mode filters to show_ready.
 */
import React from 'react';

const AGENT_COLORS = {
  nova: '#22d3ee',
  orion: '#f59e0b',
  vega: '#22c55e',
  atlas: '#ef4444',
  producer: '#60a5fa',
  quill: '#d4a574',
  scribe: '#a78bfa',
  delta: '#94a3b8',
};

const CAST_AGENTS = new Set(['nova', 'orion', 'vega', 'atlas']);

function fmt(iso) {
  if (!iso) return '--:--';
  try { return new Date(iso).toISOString().slice(11, 19) + 'Z'; } catch { return iso; }
}

function ConfidenceBadge({ value }) {
  if (value == null) return null;
  const v = Number(value);
  const color = v >= 0.8 ? '#4de2c0' : v >= 0.55 ? '#facc15' : '#ef4444';
  return (
    <span style={{
      fontSize: 9, letterSpacing: '0.14em', color,
      padding: '2px 6px', borderRadius: 3, border: `1px solid ${color}40`,
      fontFamily: 'ui-monospace, Menlo, monospace',
    }}>
      c:{v.toFixed(2)}
    </span>
  );
}

function VisibilityBadge({ value }) {
  const color = value === 'show_ready' ? '#4de2c0' : value === 'blocked' ? '#ef4444' : '#d4a574';
  return (
    <span style={{
      fontSize: 9, letterSpacing: '0.2em', color, fontFamily: 'ui-monospace, Menlo, monospace',
    }}>
      {value.toUpperCase().replace('_', ' ')}
    </span>
  );
}

export default function ShowTranscript({ messages, adminMode }) {
  if (!messages?.length) {
    return (
      <div style={{ color: 'rgba(200,210,230,0.45)', fontSize: 13, fontStyle: 'italic', padding: '10px 0', fontFamily: 'Georgia, serif' }}>
        No messages yet. Set a topic from the producer console to begin.
      </div>
    );
  }

  return (
    <div data-testid="show-transcript" style={{ maxHeight: 600, overflowY: 'auto', paddingRight: 6 }}>
      {messages.map((m) => {
        const color = AGENT_COLORS[m.from_agent] || '#dbe3f2';
        const respondingTo = m.responding_to && CAST_AGENTS.has(m.from_agent)
          && CAST_AGENTS.has(m.responding_to) && m.responding_to !== m.from_agent
          ? m.responding_to
          : null;
        const respondingColor = respondingTo ? (AGENT_COLORS[respondingTo] || '#dbe3f2') : null;
        const indent = respondingTo ? 22 : 0;
        return (
          <div
            key={m.id}
            data-testid={`transcript-msg-${m.id}`}
            data-responding-to={respondingTo || undefined}
            style={{
              marginBottom: 14,
              marginLeft: indent,
              padding: '10px 12px',
              background: 'rgba(10,14,22,0.6)',
              borderLeft: `3px solid ${color}`,
              borderRadius: 4,
              position: 'relative',
            }}
          >
            {respondingTo && (
              <div
                data-testid={`transcript-thread-${m.id}`}
                style={{
                  position: 'absolute',
                  left: -14,
                  top: -6,
                  bottom: -6,
                  width: 12,
                  borderLeft: `2px dashed ${respondingColor}55`,
                  borderTop: `2px dashed ${respondingColor}55`,
                  borderTopLeftRadius: 6,
                }}
              />
            )}
            {respondingTo && (
              <div style={{
                fontSize: 9,
                letterSpacing: '0.18em',
                color: respondingColor,
                fontFamily: 'ui-monospace, Menlo, monospace',
                textTransform: 'uppercase',
                marginBottom: 6,
                opacity: 0.85,
              }}>
                ↳ responding to {respondingTo}
                {m.responding_to_excerpt && (
                  <span style={{
                    color: 'rgba(200,210,230,0.55)',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    marginLeft: 8,
                  }}>
                    "{m.responding_to_excerpt}"
                  </span>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, letterSpacing: '0.22em', color, fontWeight: 600, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                {m.from_agent.toUpperCase()}
                {m.to_agent && (
                  <span style={{ opacity: 0.55, marginLeft: 8 }}>→ {m.to_agent}</span>
                )}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {adminMode && <VisibilityBadge value={m.visibility} />}
                <ConfidenceBadge value={m.confidence} />
                {m.evidence_ids?.length > 0 && (
                  <span style={{ fontSize: 9, letterSpacing: '0.14em', color: '#60a5fa', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    ev:{m.evidence_ids.length}
                  </span>
                )}
                <span style={{ fontSize: 10, color: 'rgba(200,210,230,0.45)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {fmt(m.created_at)}
                </span>
              </div>
            </div>
            <div style={{
              fontFamily: 'Georgia, "Iowan Old Style", serif',
              fontSize: 14, lineHeight: 1.6, color: '#e5ebf3',
              whiteSpace: 'pre-wrap',
            }}>
              {m.text}
            </div>
            {adminMode && m.claims?.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(200,210,230,0.5)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                {m.claims.length} claim{m.claims.length === 1 ? '' : 's'} extracted
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
